import { Box, Divider, Link as MuiLink, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import { FC, useRef } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import BankResetAnnouncement from "web/Layout/BankResetAnnouncement";
import ErrorBoundary from "web/Layout/ErrorBoundary";
import MessagesTrigger from "web/Layout/MainHeader/Shared/MessagesTrigger";
import SearchAutocomplete from "web/Layout/SearchAutocomplete";
import __ from "web/Layout/Translations";

import urls from "web/constants/urls";

import { moduleHeaderPaths } from "../../headerPaths";
import CartTrigger from "../CartTrigger";
import MBHeader from "../MBHeader";
import MainMenu from "../MainMenu";
import mainMenuItemClasses from "../MainMenu/MainMenuItem/mainMenuItem.scss";
import WishlistTrigger from "../WishlistTrigger";
import useHeaderVisibility from "./hooks/useHeaderVisibility";
import classes from "./mainHeaderDashboard.scss";

interface IMainHeaderDashboardProps {
  shouldShowMessage: boolean;
}

const MainHeaderDashboard: FC<IMainHeaderDashboardProps> = ({
  shouldShowMessage,
}) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { isVisible } = useHeaderVisibility();
  const theme = useTheme();
  const moduleHeaderRef = useRef<HTMLDivElement>(null);

  const moduleHeader = moduleHeaderPaths.find((item) =>
    pathname.startsWith(item.path)
  );

  return (
    <>
      <MBHeader />
      {moduleHeader ? (
        <Box
          sx={{
            background: theme.palette.background.paper,
            position: "sticky",
            top: 64,
            zIndex: theme.zIndex.appBar - 1,
            transition: "transform 0.3s",
            ...(!isVisible && { transform: "translateY(-100%)" }),
          }}
          ref={moduleHeaderRef}
        >
          <Box maxWidth="1920px" margin="0 auto" padding="0.75rem 3rem">
            {moduleHeader.path ? (
              <MuiLink
                color="text.primary"
                underline="none"
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "none",
                    color: "text.secondary",
                  },
                }}
                onClick={() => {
                  history.push(moduleHeader.path);
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }}
              >
                <Typography variant="h6">{__(moduleHeader.label)}</Typography>
              </MuiLink>
            ) : (
              <Typography variant="h6">{__(moduleHeader.label)}</Typography>
            )}
          </Box>
        </Box>
      ) : (
        <div
          className={classes.dashboardHeader}
          style={{
            transform: isVisible ? "translateY(0)" : "translateY(-100%)",
          }}
        >
          <div className={classes.header}>
            <div className={`containerDashboard ${classes.headerRow}`}>
              <div className={classes.heading}>
                <MuiLink
                  component={Link}
                  to={urls.home}
                  className={`${mainMenuItemClasses.link} ${classes.cafeteriaLink}`}
                  color="text.primary"
                  underline="none"
                >
                  <Typography variant="h6" sx={{ py: "11px" }}>
                    {__("Kafeteria")}
                  </Typography>
                </MuiLink>
                <Divider orientation="vertical" flexItem sx={{ pl: 3 }} />
              </div>
              <div className={classes.headerUI}>
                <ErrorBoundary smallTextSize>
                  <SearchAutocomplete className={classes.search} />
                </ErrorBoundary>
                <ul className={classes.list}>
                  <li className={classes.item}>
                    <ErrorBoundary smallTextSize>
                      <MessagesTrigger />
                    </ErrorBoundary>
                  </li>
                  <li className={classes.item}>
                    <ErrorBoundary smallTextSize>
                      <WishlistTrigger />
                    </ErrorBoundary>
                  </li>
                  <li className={classes.item}>
                    <ErrorBoundary smallTextSize>
                      <CartTrigger />
                    </ErrorBoundary>
                  </li>
                </ul>
              </div>
            </div>

            <div className={classes.menuWrapper}>
              <ErrorBoundary smallTextSize>
                <MainMenu />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      )}
      <BankResetAnnouncement shouldShowMessage={shouldShowMessage} />
    </>
  );
};

export default MainHeaderDashboard;
